import type {
  DetectionBox,
  DetectionCategory,
  MemoryProfile,
  OcrLine,
  PresetConfig,
  Rect
} from "@aimosaic/core";
import { dedupeBoxes } from "@aimosaic/core";

interface PatternDefinition {
  category: DetectionCategory;
  expression: RegExp;
}

export interface PatternDetectionDiagnostics {
  contextCandidateCount: number;
  recoveredCandidateCount: number;
  layoutCandidateCount: number;
}

export interface PatternDetectionResult {
  boxes: DetectionBox[];
  diagnostics: PatternDetectionDiagnostics;
}

interface CandidateBox extends DetectionBox {
  lineIndex: number;
  start: number;
  end: number;
  candidateType: "pattern" | "context" | "recovered";
}

interface WordSpan {
  text: string;
  start: number;
  end: number;
  bbox: Rect;
}

const PATTERNS: PatternDefinition[] = [
  {
    category: "email",
    expression: /\b[A-Z0-9._%+-]+\s*@\s*[A-Z0-9-]+(?:\s*\.\s*[A-Z0-9-]+)+\b/gi
  },
  {
    category: "ip",
    expression:
      /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g
  },
  {
    category: "url",
    expression: /\bhttps?:\/\/[^\s"'<>]+/gi
  },
  {
    category: "url",
    expression:
      /\b(?!(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(?::\d{2,5})?(?:\/[^\s"'<>]*)?\b/gi
  },
  {
    category: "resident-id",
    expression: /\b\d{6}[-\s]?[1-8]\d{6}\b/g
  },
  {
    category: "phone",
    expression:
      /(?<![\w])(?:\+82[-\s]?0?1\d[-\s]?\d{3,4}[-\s]?\d{4}|0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4})(?!\w)/g
  },
  {
    category: "token",
    expression: /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{3,}\b/g
  },
  {
    category: "secret",
    expression: /\b(?:sk|pk|rk|ghp|gho|ghu|glpat)-[A-Za-z0-9_-]{10,}\b/g
  },
  {
    category: "secret",
    expression: /\b(?:sk|pk|rk)_[A-Za-z0-9_]{10,}\b/g
  },
  {
    category: "secret",
    expression: /\b[A-Za-z][A-Za-z0-9]*_(?:live|test|prod|dev|secret|key|token|pass|demo)_[A-Za-z0-9_]{10,}\b/g
  }
];

const RECOVERABLE_DOMAIN_EXPRESSION =
  /(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z0-9]{2,63}/g;
const DOMAIN_TLDS = new Set(["com", "c0m", "con", "dev", "org", "0rg", "ord", "kr", "net", "io", "local"]);
const KNOWN_DOMAIN_TLDS = ["com", "dev", "org", "kr", "net", "io", "local"];
const JOINED_URL_SUFFIX_EXPRESSION = /(?:wildc(?:ard)?|active|local|DSM|활성|[가-힣])/i;

export function detectPatternBoxes(
  lines: OcrLine[],
  preset: PresetConfig,
  memory: MemoryProfile = {}
): DetectionBox[] {
  return detectPatternBoxesWithDiagnostics(lines, preset, memory).boxes;
}

export function detectPatternBoxesWithDiagnostics(
  lines: OcrLine[],
  preset: PresetConfig,
  memory: MemoryProfile = {}
): PatternDetectionResult {
  const enabled = new Set<DetectionCategory>(preset.categories);
  const ignoredTexts = new Set((memory.ignoredTexts ?? []).map(normalizeText));
  const candidates: CandidateBox[] = [];
  let contextCandidateCount = 0;
  let recoveredCandidateCount = 0;
  let layoutCandidateCount = 0;

  for (const [lineIndex, line] of lines.entries()) {
    const searchableLine = normalizeOcrLine(line);
    for (const pattern of PATTERNS) {
      if (!enabled.has(pattern.category)) continue;
      candidates.push(...matchPattern(searchableLine, lineIndex, pattern, ignoredTexts, candidates.length));
    }
    if (enabled.has("url")) {
      const recoveredCandidates = matchRecoveredUrls(searchableLine, lineIndex, ignoredTexts, candidates.length);
      recoveredCandidateCount += recoveredCandidates.length;
      candidates.push(...recoveredCandidates);
    }

    const contextCandidates = matchContext(searchableLine, lineIndex, enabled, ignoredTexts, candidates.length);
    contextCandidateCount += contextCandidates.length;
    candidates.push(...contextCandidates);
  }

  if (enabled.has("url")) {
    const layoutCandidates = matchLayoutRecoveredUrls(lines, candidates, ignoredTexts, candidates.length);
    layoutCandidateCount += layoutCandidates.length;
    candidates.push(...layoutCandidates);
  }

  const adjustedCandidates = widenPartialRepeatedUrlCandidates(candidates);
  const boxes = suppressNestedCandidates(adjustedCandidates).map(({ lineIndex, start, end, candidateType, ...box }) => box);

  return {
    boxes: dedupeBoxes(boxes, 0.2),
    diagnostics: { contextCandidateCount, recoveredCandidateCount, layoutCandidateCount }
  };
}

function matchPattern(
  line: OcrLine,
  lineIndex: number,
  pattern: PatternDefinition,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  const expression = new RegExp(pattern.expression.source, pattern.expression.flags);
  const boxes: CandidateBox[] = [];
  for (const match of line.text.matchAll(expression)) {
    const rawText = match[0];
    const start = match.index ?? 0;
    const trimmed =
      pattern.category === "url"
        ? trimUrlMatch(line.text, start, start + rawText.length)
        : trimSensitiveMatch(rawText, start, start + rawText.length);
    if (pattern.category === "email" && !isValidEmailMatch(line.text, trimmed.start, trimmed.end)) continue;
    if (pattern.category === "url" && isEmailDomainMatch(line.text, trimmed.start)) continue;
    if (pattern.category === "url" && looksLikeFileReferenceText(trimmed.text)) continue;
    if (pattern.category === "ip" && looksLikeVersionOrIdentifier(line.text, trimmed.start, trimmed.end)) continue;
    const text = normalizeMatchedText(pattern.category, trimmed.text);
    if (pattern.category === "url" && !isValidUrlValue(text)) continue;
    if (pattern.category === "url" && isEmailLocalPartUrlFragment(line.text, trimmed.end)) continue;
    if (pattern.category === "ip" && isDocumentationVersionPlaceholder(text)) continue;
    const boxRange = pattern.category === "url" ? extendUrlBoxRange(line, trimmed.start, trimmed.end) : trimmed;
    if (ignoredTexts.has(normalizeText(text))) continue;
    boxes.push(
      createCandidateBox({
        category: pattern.category,
        line,
        lineIndex,
        start: boxRange.start,
        end: boxRange.end,
        text,
        confidence: 0.94,
        candidateType: "pattern",
        idIndex: idOffset + boxes.length
      })
    );
  }
  return boxes;
}

function normalizeMatchedText(category: DetectionCategory, text: string): string {
  if (category === "email") {
    return text.replace(/\s+/g, "");
  }
  if (category === "url") {
    return normalizeUrlOcrValue(normalizeOcrText(text));
  }
  if (category === "token" || category === "secret") {
    return normalizeSensitiveOcrValue(text);
  }
  return normalizeOcrText(text);
}

function isEmailDomainMatch(text: string, start: number): boolean {
  if (text[start - 1] !== "@") return false;
  const localPart = emailLocalPartBeforeAt(text, start - 1);
  return Boolean(localPart && hasEmailBoundary(text, localPart.start));
}

function looksLikeFileReferenceText(text: string): boolean {
  const value = text.trim();
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return false;
  if (/^\/(?:Users|Volumes|private|tmp|var)\//i.test(value)) return true;
  if (/^[A-Z]:\\/i.test(value)) return true;
  return /\.(?:txt|md|markdown|png|jpe?g|webp|gif|pdf|docx?|xlsx?|csv|log|json|ya?ml)$/i.test(value);
}

function isValidUrlValue(text: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text)) return true;
  if (/^(?:localhost|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?(?:[/?#][^\s]*)?$/i.test(text)) return true;
  const domain = text.split(/[/?#]/, 1)[0]?.replace(/:\d{2,5}$/, "") ?? "";
  return isRecoverableDomainText(domain);
}

function isEmailLocalPartUrlFragment(text: string, end: number): boolean {
  return /^\s*@/.test(text.slice(end, end + 8));
}

function looksLikeVersionOrIdentifier(text: string, start: number, end: number): boolean {
  const before = text.slice(Math.max(0, start - 16), start).toLowerCase();
  const after = text.slice(end, Math.min(text.length, end + 16)).toLowerCase();
  const nearby = `${before} ${after}`;
  if (/[a-z0-9_]$/.test(text[start - 1] ?? "")) return true;
  if (/^(?:\.\d+|[a-z0-9_])/.test(text.slice(end))) return true;
  return /(?:\bv\s*$|version|버전|oid|checksum|릴리즈|release)/i.test(nearby);
}

function isDocumentationVersionPlaceholder(text: string): boolean {
  return text.trim() === "1.2.3.4";
}

function matchRecoveredUrls(
  line: OcrLine,
  lineIndex: number,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  const lineMatches = matchRecoveredTextUrls(line, lineIndex, ignoredTexts, idOffset);
  const wordMatches = matchRecoveredWordUrls(line, lineIndex, ignoredTexts, idOffset + lineMatches.length);
  const reconstructedMatches = matchReconstructedWordUrls(
    line,
    lineIndex,
    ignoredTexts,
    idOffset + lineMatches.length + wordMatches.length
  );
  const seen = new Set<string>();
  return [...lineMatches, ...wordMatches, ...reconstructedMatches].filter((box) => {
    const key = `${box.start}:${box.end}:${normalizeText(box.text ?? "")}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function matchRecoveredTextUrls(
  line: OcrLine,
  lineIndex: number,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  return createRecoveredUrlBoxes(line.text, line, lineIndex, 0, ignoredTexts, idOffset);
}

function matchRecoveredWordUrls(
  line: OcrLine,
  lineIndex: number,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  if (!line.words?.length) return [];
  const boxes: CandidateBox[] = [];
  for (const span of wordSpans(line)) {
    if (!/[@|]/.test(span.text) && !/[A-Za-z]0[A-Za-z]/.test(span.text)) continue;
    boxes.push(
      ...createRecoveredUrlBoxes(span.text, line, lineIndex, span.start, ignoredTexts, idOffset + boxes.length)
    );
  }

  return boxes;
}

function matchReconstructedWordUrls(
  line: OcrLine,
  lineIndex: number,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  if (!line.words?.length) return [];
  const spans = wordSpans(line);
  const boxes: CandidateBox[] = [];
  for (let index = 0; index < spans.length; index += 1) {
    const candidate = buildDomainFromAdjacentWords(spans, index);
    if (!candidate) continue;
    if (ignoredTexts.has(normalizeText(candidate.text))) continue;
    if (!hasUrlishRowContext(line.text) && candidate.labels.length < 3) continue;
    boxes.push(
      createCandidateBox({
        category: "url",
        line,
        lineIndex,
        start: candidate.start,
        end: candidate.end,
        text: candidate.text,
        confidence: 0.9,
        candidateType: "recovered",
        idIndex: idOffset + boxes.length
      })
    );
  }
  return boxes;
}

function buildDomainFromAdjacentWords(
  spans: WordSpan[],
  startIndex: number
): { text: string; start: number; end: number; labels: string[] } | null {
  const labels: string[] = [];
  let endIndex = startIndex;
  for (let index = startIndex; index < Math.min(spans.length, startIndex + 7); index += 1) {
    const span = spans[index];
    const label = cleanDomainLabel(span.text);
    if (!label) {
      if ((span.text === "." || span.text === "-") && labels.length > 0) {
        endIndex = index;
        continue;
      }
      break;
    }
    labels.push(label);
    endIndex = index;
    if (labels.length >= 2 && isDomainTld(label)) {
      return {
        text: labels.join("."),
        start: spans[startIndex].start,
        end: spans[endIndex].end,
        labels
      };
    }
  }
  return null;
}

function cleanDomainLabel(text: string): string {
  return text
    .replace(/^[^A-Za-z0-9]+/, "")
    .replace(/[^A-Za-z0-9]+$/, "")
    .toLowerCase();
}

function isDomainTld(text: string): boolean {
  return DOMAIN_TLDS.has(text.toLowerCase());
}

function createRecoveredUrlBoxes(
  textScope: string,
  line: OcrLine,
  lineIndex: number,
  startOffset: number,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  const boxes: CandidateBox[] = [];
  const expression = new RegExp(RECOVERABLE_DOMAIN_EXPRESSION.source, RECOVERABLE_DOMAIN_EXPRESSION.flags);
  for (const match of textScope.matchAll(expression)) {
    const start = startOffset + (match.index ?? 0);
    const trimmed = trimUrlMatch(line.text, start, start + match[0].length);
    const text = trimmed.text;
    if (!isRecoverableDomainText(text)) continue;
    if (looksLikeFileReferenceText(text)) continue;
    if (isEmailDomainMatch(line.text, trimmed.start)) continue;
    if (ignoredTexts.has(normalizeText(text))) continue;
    boxes.push(
      createCandidateBox({
        category: "url",
        line,
        lineIndex,
        start: trimmed.start,
        end: trimmed.end,
        text,
        confidence: 0.96,
        candidateType: "recovered",
        idIndex: idOffset + boxes.length
      })
    );
  }
  return boxes;
}

function isRecoverableDomainText(text: string): boolean {
  const labels = text.split(".");
  if (labels.length < 2) return false;
  const tld = labels.at(-1) ?? "";
  if (!/[A-Za-z]/.test(tld)) return false;
  if (!isDomainTld(tld)) return false;
  if (labels.every((label) => /^\d+$/.test(label))) return false;
  return labels.every(
    (label) =>
      label.length > 0 &&
      label.length <= 63 &&
      /^[A-Za-z0-9-]+$/.test(label) &&
      !label.startsWith("-") &&
      !label.endsWith("-")
  );
}

function isValidEmailMatch(text: string, start: number, end: number): boolean {
  const atIndex = text.indexOf("@", start);
  if (atIndex < 0 || atIndex >= end) return false;
  const localPart = emailLocalPartBeforeAt(text, atIndex);
  if (!localPart) return false;
  return hasEmailBoundary(text, localPart.start);
}

function emailLocalPartBeforeAt(text: string, atIndex: number): { start: number; end: number } | null {
  let end = atIndex;
  while (end > 0 && /\s/.test(text[end - 1])) {
    end -= 1;
  }

  let start = end;
  while (start > 0 && /[A-Z0-9._%+-]/i.test(text[start - 1])) {
    start -= 1;
  }

  return start < end ? { start, end } : null;
}

function hasEmailBoundary(text: string, start: number): boolean {
  if (start === 0) return true;
  return /[\s([<{,;:'"=]/.test(text[start - 1]);
}

function matchContext(
  line: OcrLine,
  lineIndex: number,
  enabled: Set<DetectionCategory>,
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  const labelExpression =
    /\b(?:api[\s_-]?key|secret(?:\s+key)?|client[\s_-]?secret|password|passphrase|access[\s_-]?token|refresh[\s_-]?token|token|authorization|bearer|phone|telephone|mobile|tel|birth(?:day|\s+date)?|d[o0]b|0{2}8|resident[\s_-]?id|rrn)\b|(?:API\s*키|비밀\s*키|비밀번호|패스워드|암호|토큰|시크릿|전화번호|대표\s*전화|대표\s*[A-Za-z가-힣|]{1,8}|휴대폰|연락처|국제\s*표기|생년월일|생일|주민등록번호|외국인등록번호)/gi;
  const boxes: CandidateBox[] = [];

  for (const labelMatch of line.text.matchAll(labelExpression)) {
    const label = labelMatch[0].toLowerCase();
    const category = contextCategory(label);
    if (!enabled.has(category)) continue;

    const labelEnd = (labelMatch.index ?? 0) + labelMatch[0].length;
    const valueMatch = findUnparsedContextTail(line.text, labelEnd, label, category)
      ?? findContextValue(line.text, labelEnd, category);
    if (!valueMatch) continue;
    if (ignoredTexts.has(normalizeText(valueMatch.text))) continue;
    if (
      boxes.some(
        (box) =>
          box.category === category &&
          box.start === valueMatch.start &&
          box.end === valueMatch.end
      )
    ) {
      continue;
    }

    boxes.push(
      createCandidateBox({
        category,
        line,
        lineIndex,
        start: valueMatch.start,
        end: valueMatch.end,
        text: valueMatch.text,
        confidence: 0.91,
        candidateType: "context",
        idIndex: idOffset + boxes.length,
        requiresReview: true
      })
    );
  }

  return boxes;
}

function findUnparsedContextTail(
  text: string,
  startAt: number,
  label: string,
  category: Extract<DetectionCategory, "secret" | "token" | "phone" | "resident-id">
): { text: string; start: number; end: number } | null {
  const highSignalSecret = category === "secret" && /(?:api[\s_-]?key|secret|password|비밀|비밀번호|시크릿)/i.test(label);
  const highSignalToken = category === "token" && /(?:token|authorization|bearer|토큰)/i.test(label);
  if (!highSignalSecret && !highSignalToken) {
    return null;
  }

  let offset = startAt;
  const separator = text.slice(offset).match(/^\s*(?::|=)?\s*/);
  if (separator) offset += separator[0].length;
  offset = skipNestedSecretLabel(text, offset);
  if (category === "token") {
    const bearer = text.slice(offset).match(/^Bearer\s+/i);
    if (bearer) offset += bearer[0].length;
  }
  const tail = trimSensitiveTail(text.slice(offset));
  if (!tail || !looksLikeUnparsedSensitiveTail(tail)) return null;
  const start = offset + text.slice(offset).indexOf(tail);
  return { text: normalizeUnparsedSensitiveText(tail), start, end: start + tail.length };
}

function normalizeUnparsedSensitiveText(text: string): string {
  if (/^eyJ[A-Za-z0-9_-]+\./.test(normalizeOcrText(text).trim())) return normalizeSensitiveOcrValue(text);
  const parts = text.split(/[\s._-]+/).filter(Boolean);
  return normalizeSensitiveOcrValue(looksSensitiveSeparatedValue(parts) ? parts.join("_") : text);
}

function trimSensitiveTail(text: string): string {
  return text.trim().replace(/^[^\w]+/, "").replace(/[.,;)\]]+$/, "").trim();
}

function looksLikeUnparsedSensitiveTail(text: string): boolean {
  const normalized = text.toLowerCase();
  if (normalized.length < 10) return false;
  if (/\b(?:free|none|null|true|false|policy|generated|years?\s+ago)\b/.test(normalized)) return false;
  if (/^eyj[a-z0-9_-]+\./i.test(normalized)) return true;
  return /[0-9a-f]{8,}/i.test(normalized) || /[_=\-¢©]/.test(normalized);
}

function contextCategory(label: string): Extract<DetectionCategory, "secret" | "token" | "phone" | "resident-id"> {
  if (/phone|telephone|mobile|tel|전화|대표|휴대폰|연락처|국제/.test(label)) {
    return "phone";
  }
  if (/birth|d[o0]b|^0{2}8$|resident|rrn|생년월일|생일|주민|외국인/.test(label)) {
    return "resident-id";
  }
  if (/key|secret|password|passphrase|비밀|비밀번호|패스워드|암호|시크릿/.test(label) && !/token|토큰/.test(label)) {
    return "secret";
  }
  return "token";
}

function findContextValue(
  text: string,
  startAt: number,
  category: Extract<DetectionCategory, "secret" | "token" | "phone" | "resident-id">
): { text: string; start: number; end: number } | null {
  let offset = startAt;
  const separator = text.slice(offset).match(/^\s*(?::|=)?\s*/);
  if (separator) offset += separator[0].length;
  offset = skipNestedSecretLabel(text, offset);

  if (category === "token") {
    const bearer = text.slice(offset).match(/^Bearer\s+/i);
    if (bearer) offset += bearer[0].length;
  }

  if (category === "phone") {
    return findContextPatternValue(
      text,
      offset,
      /(\+82[-\s]?0?1\d[-\s]?\d{3,4}[-\s]?\d{4}|0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4})/
    ) ?? findLenientPhoneValue(text, offset);
  }

  if (category === "resident-id") {
    return findContextPatternValue(
      text,
      offset,
      /(\d{6}[-\s]?[1-8]\d{6}|(?:19|20)\d{2}[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01]))/
    ) ?? findLenientResidentValue(text, offset);
  }

  const separatedValue = findSeparatedSecretValue(text, offset, category);
  if (separatedValue) return separatedValue;

  const valueMatch = text.slice(offset).match(/["']?([A-Za-z0-9][A-Za-z0-9._~+/=-]{5,})["']?/);
  if (!valueMatch || valueMatch.index === undefined) return null;
  const value = valueMatch[1];
  if (!looksSensitiveValue(value)) return null;
  const start = offset + valueMatch.index + valueMatch[0].indexOf(value);
  return { text: value, start, end: start + value.length };
}

function skipNestedSecretLabel(text: string, offset: number): number {
  const nestedLabel = text.slice(offset).match(/^\]?\s*(?:\[[A-Z]+\]\s*)?(?:api[\s_-]?key|secret(?:\s+key)?|client[\s_-]?secret|password|비밀\s*키|비밀번호|시크릿)\s*(?::|=)\s*/i);
  return nestedLabel ? offset + nestedLabel[0].length : offset;
}

function findSeparatedSecretValue(
  text: string,
  offset: number,
  category: Extract<DetectionCategory, "secret" | "token" | "phone" | "resident-id">
): { text: string; start: number; end: number } | null {
  if (category !== "secret" && category !== "token") return null;
  const valueMatch = text.slice(offset).match(/([A-Za-z0-9]{2,}(?:[\s._-]+[A-Za-z0-9]{2,}){1,6})/);
  if (!valueMatch || valueMatch.index === undefined) return null;
  const rawValue = valueMatch[1].trim();
  const parts = rawValue.split(/[\s._-]+/).filter(Boolean);
  if (!looksSensitiveSeparatedValue(parts)) return null;
  const normalized = parts.join("_");
  const start = offset + valueMatch.index + valueMatch[0].indexOf(valueMatch[1]);
  return { text: normalized, start, end: start + valueMatch[1].length };
}

function looksSensitiveSeparatedValue(parts: string[]): boolean {
  if (parts.length < 3) return false;
  const normalized = parts.join("_").toLowerCase();
  if (!/(?:^|_)(?:sk|pk|rk|live|test|prod|dev|secret|key|token|pass|demo)(?:_|$)/.test(normalized)) return false;
  return parts.some((part) => part.length >= 10 && /[0-9]/.test(part));
}

function findContextPatternValue(
  text: string,
  offset: number,
  expression: RegExp
): { text: string; start: number; end: number } | null {
  const valueMatch = text.slice(offset).match(expression);
  if (!valueMatch || valueMatch.index === undefined) return null;
  const value = valueMatch[1];
  const start = offset + valueMatch.index + valueMatch[0].indexOf(value);
  return { text: value, start, end: start + value.length };
}

function findLenientPhoneValue(text: string, offset: number): { text: string; start: number; end: number } | null {
  const normalized = normalizePhoneOcrText(text);
  const valueMatch = normalized.slice(offset).match(/(\+82[-\s]?0?1\d[-\s]?\d{3,4}[-\s]?\d{4}|0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4})/);
  if (valueMatch && valueMatch.index !== undefined) {
    const value = valueMatch[1];
    const start = offset + valueMatch.index + valueMatch[0].indexOf(value);
    return { text: value, start, end: start + value.length };
  }

  const correctedLandline = normalized
    .slice(offset)
    .match(/\b9(0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4})\b/);
  if (!correctedLandline || correctedLandline.index === undefined) return null;
  const value = correctedLandline[1];
  const start = offset + correctedLandline.index + correctedLandline[0].indexOf(value);
  return { text: value, start, end: start + value.length };
}

function findLenientResidentValue(text: string, offset: number): { text: string; start: number; end: number } | null {
  const normalized = normalizeResidentOcrText(text);
  const valueMatch = normalized
    .slice(offset)
    .match(/(\d{6}[-\s]?[1-8]\d{6}|(?:19|20)\d{2}[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01]))/);
  if (!valueMatch || valueMatch.index === undefined) return null;
  const value = valueMatch[1];
  const start = offset + valueMatch.index + valueMatch[0].indexOf(value);
  return { text: value, start, end: start + value.length };
}

function normalizePhoneOcrText(text: string): string {
  return normalizeOcrText(text).replace(/[Oo]/g, "0");
}

function normalizeResidentOcrText(text: string): string {
  return normalizeOcrText(text).replace(/[Oo]/g, "0").replace(/B/g, "8");
}

function normalizeSensitiveOcrValue(text: string): string {
  let value = normalizeOcrText(text)
    .replace(/[¢©]/g, "c")
    .replace(/(?<=[A-Za-z0-9])=(?=[A-Za-z0-9])/g, "-");

  if (/^[0-9a-fhIl/]{8}-[0-9a-fhIl/]{4}-[0-9a-fhIl/]{4}-[0-9a-fhIl/]{4}-[0-9a-fhIl/]{12}$/i.test(value)) {
    value = value.replace(/[lI]/g, "1").replace(/\//g, "7").replace(/h/g, "b").replace(/H/g, "B");
  }

  return value;
}

function normalizeUrlOcrValue(text: string): string {
  return text
    .replace(/\/1(?=ogin(?:$|[/?#]))/i, "/l")
    .replace(/\/1login(?=$|[/?#])/i, "/login")
    .replace(/\/vl(?=\/)/i, "/v1")
    .replace(/\/v1l(?=\/)/i, "/v1");
}

function looksSensitiveValue(value: string): boolean {
  if (value.length < 8) return false;
  if (!/[A-Za-z]/.test(value)) return false;
  return /[0-9]/.test(value) || /[-_.=+/]/.test(value);
}

function normalizeOcrLine(line: OcrLine): OcrLine {
  const text = normalizeOcrText(line.text);
  if (text === line.text && !line.words?.some((word) => normalizeOcrText(word.text) !== word.text)) return line;
  return {
    ...line,
    text,
    words: line.words?.map((word) => ({ ...word, text: normalizeOcrText(word.text) }))
  };
}

function normalizeOcrText(text: string): string {
  return text.replace(/[\u2010-\u2015\u2212]/g, "-");
}

function createCandidateBox({
  category,
  line,
  lineIndex,
  start,
  end,
  text,
  confidence,
  candidateType,
  idIndex,
  requiresReview
}: {
  category: DetectionCategory;
  line: OcrLine;
  lineIndex: number;
  start: number;
  end: number;
  text: string;
  confidence: number;
  candidateType: CandidateBox["candidateType"];
  idIndex: number;
  requiresReview?: boolean;
}): CandidateBox {
  return {
    id: `${category}-${idIndex}-${start}`,
    category,
    bbox: rangeToRect(line, start, end, category),
    confidence,
    source: "local-ocr",
    text,
    reason: detectorCandidateReason(category, candidateType),
    requiresReview,
    action: "mask",
    lineIndex,
    start,
    end,
    candidateType
  };
}

function detectorCandidateReason(category: DetectionCategory, candidateType: CandidateBox["candidateType"]): string {
  if (candidateType === "context") return `${category} label/value context`;
  if (candidateType === "recovered") return `${category} OCR recovery`;
  return `${category} pattern match`;
}

function suppressNestedCandidates(candidates: CandidateBox[]): CandidateBox[] {
  return candidates.filter((candidate) => {
    if (candidate.category === "ip") {
      return !candidates.some(
        (other) =>
          other.category === "url" &&
          other.lineIndex === candidate.lineIndex &&
          other.start <= candidate.start &&
          other.end >= candidate.end
      );
    }

    if (candidate.category === "url") {
      const insideEmail = candidates.some(
        (other) =>
          other.category === "email" &&
          other.lineIndex === candidate.lineIndex &&
          other.start <= candidate.start &&
          other.end >= candidate.end
      );
      if (insideEmail) return false;

      const insideSensitiveValue = candidates.some(
        (other) =>
          (other.category === "token" || other.category === "secret") &&
          other.lineIndex === candidate.lineIndex &&
          rangesOverlap(candidate.start, candidate.end, other.start, other.end)
      );
      if (insideSensitiveValue) return false;

      return !candidates.some(
        (other) =>
          other !== candidate &&
          other.category === "url" &&
          other.lineIndex === candidate.lineIndex &&
          other.start <= candidate.start &&
          other.end >= candidate.end &&
          other.end - other.start > candidate.end - candidate.start
      );
    }

    if (candidate.candidateType === "pattern") {
      return !candidates.some(
        (other) =>
          other.candidateType === "context" &&
          other.category === candidate.category &&
          other.lineIndex === candidate.lineIndex &&
          rangesOverlap(candidate.start, candidate.end, other.start, other.end)
      );
    }

    return true;
  });
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function trimSensitiveMatch(text: string, start: number, end: number): { text: string; start: number; end: number } {
  let nextText = text;
  let nextStart = start;
  let nextEnd = end;
  while (/[.,;:)]$/.test(nextText)) {
    nextText = nextText.slice(0, -1);
    nextEnd -= 1;
  }
  while (/^[(]/.test(nextText)) {
    nextText = nextText.slice(1);
    nextStart += 1;
  }
  return { text: nextText, start: nextStart, end: nextEnd };
}

function trimUrlMatch(lineText: string, start: number, end: number): { text: string; start: number; end: number } {
  let nextEnd = end;
  const rawText = lineText.slice(start, end);
  const suffix = rawText.search(/(?:->|→|·|\s+-\s+|-DSM|wildc(?:ard)?|active|DSM|Authorization|Bearer|활성|[가-힣])/i);
  if (suffix >= 0) {
    nextEnd = start + suffix;
  }
  nextEnd = trimJoinedTldSuffix(lineText, start, nextEnd);
  let nextText = lineText.slice(start, nextEnd);
  while (/[.,;:)\]-]$/.test(nextText)) {
    nextText = nextText.slice(0, -1);
    nextEnd -= 1;
  }
  return trimSensitiveMatch(nextText, start, nextEnd);
}

function trimJoinedTldSuffix(lineText: string, start: number, end: number): number {
  const rawText = lineText.slice(start, end);
  const domainMatch = findLastDomainTail(rawText);
  if (!domainMatch) return end;
  const domainEndInRaw = domainMatch.start + domainMatch.text.length;
  if (domainEndInRaw < rawText.length && /^[/:?#]/.test(rawText[domainEndInRaw])) return end;

  for (const tld of KNOWN_DOMAIN_TLDS) {
    const tldStart = domainMatch.text.length - tld.length;
    if (tldStart <= 0) continue;
    if (domainMatch.text.slice(tldStart).toLowerCase() !== tld) continue;
    const absoluteEnd = start + domainMatch.start + domainMatch.text.length;
    const suffixText = lineText.slice(absoluteEnd, Math.min(lineText.length, absoluteEnd + 16));
    if (JOINED_URL_SUFFIX_EXPRESSION.test(suffixText)) {
      return absoluteEnd;
    }
  }

  const labels = domainMatch.text.split(".");
  const tail = labels.at(-1) ?? "";
  const knownPrefix = KNOWN_DOMAIN_TLDS.find((tld) => tail.toLowerCase().startsWith(tld) && tail.length > tld.length);
  if (!knownPrefix) return end;
  const joinedSuffix = tail.slice(knownPrefix.length);
  if (!JOINED_URL_SUFFIX_EXPRESSION.test(joinedSuffix)) return end;
  return start + domainMatch.start + domainMatch.text.length - joinedSuffix.length;
}

function findLastDomainTail(text: string): { text: string; start: number } | null {
  const matches = [...text.matchAll(/(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z0-9]{2,63}/g)];
  const match = matches.at(-1);
  return match && match.index !== undefined ? { text: match[0], start: match.index } : null;
}

function matchLayoutRecoveredUrls(
  lines: OcrLine[],
  candidates: CandidateBox[],
  ignoredTexts: Set<string>,
  idOffset: number
): CandidateBox[] {
  const urlCandidates = candidates.filter((candidate) => candidate.category === "url");
  const rowLines = lines
    .map((line, lineIndex) => ({ line, lineIndex }))
    .filter(({ line }) => hasUrlishRowContext(line.text));
  if (rowLines.length < 4 || urlCandidates.length < 6) return [];

  const columns = clusterUrlColumns(urlCandidates);
  const boxes: CandidateBox[] = [];
  for (const column of columns) {
    if (column.lineIndexes.size < Math.max(3, rowLines.length - 1)) continue;
    for (const { line, lineIndex } of rowLines) {
      if (column.lineIndexes.has(lineIndex)) continue;
      const recovery = layoutRecoveryText(line, column.x);
      if (recovery?.text && ignoredTexts.has(normalizeText(recovery.text))) continue;
      boxes.push(
        {
          id: `url-layout-${idOffset + boxes.length}-${lineIndex}`,
          category: "url",
          bbox: { x: column.x, y: line.bbox.y, width: column.width, height: line.bbox.height },
          confidence: 0.62,
          source: "local-ocr",
          ...(recovery?.text ? { text: recovery.text } : {}),
          reason: "url repeated-row layout recovery",
          action: "mask",
          lineIndex,
          start: recovery?.start ?? -1 - boxes.length,
          end: recovery?.end ?? -1 - boxes.length,
          candidateType: "recovered"
        }
      );
    }
  }
  return boxes;
}

function clusterUrlColumns(candidates: CandidateBox[]): Array<{ x: number; width: number; lineIndexes: Set<number> }> {
  const columns: Array<{ boxes: CandidateBox[] }> = [];
  for (const candidate of candidates) {
    const centerX = candidate.bbox.x + candidate.bbox.width / 2;
    const column = columns.find((item) => {
      const itemCenter = average(item.boxes.map((box) => box.bbox.x + box.bbox.width / 2));
      return Math.abs(itemCenter - centerX) < 80;
    });
    if (column) {
      column.boxes.push(candidate);
    } else {
      columns.push({ boxes: [candidate] });
    }
  }
  return columns.map(({ boxes }) => ({
    x: average(boxes.map((box) => box.bbox.x)),
    width: average(boxes.map((box) => box.bbox.width)),
    lineIndexes: new Set(boxes.map((box) => box.lineIndex))
  }));
}

function layoutRecoveryText(line: OcrLine, columnX: number): { text: string; start: number; end: number } | undefined {
  return wordSpans(line).find((word) => Math.abs(word.bbox.x - columnX) < 90 && /[.@*:]/.test(word.text));
}

function widenPartialRepeatedUrlCandidates(candidates: CandidateBox[]): CandidateBox[] {
  const columns = clusterUrlCandidateBoxes(candidates.filter((candidate) => candidate.category === "url"));
  const replacementRects = new Map<string, Rect>();
  for (const column of columns) {
    if (column.boxes.length < 3) continue;
    const references = column.boxes.filter(isReliableUrlColumnReference);
    if (references.length < 2) continue;
    const referenceX = average(references.map((box) => box.bbox.x));
    const referenceRight = Math.max(...references.map((box) => box.bbox.x + box.bbox.width));
    const referenceWidth = referenceRight - referenceX;
    for (const box of column.boxes) {
      if (!isPartialUrlColumnCandidate(box)) continue;
      replacementRects.set(box.id, {
        x: referenceX,
        y: box.bbox.y,
        width: referenceWidth,
        height: box.bbox.height
      });
    }
  }

  return candidates.map((candidate) => {
    const bbox = replacementRects.get(candidate.id);
    return bbox ? { ...candidate, bbox } : candidate;
  });
}

function clusterUrlCandidateBoxes(candidates: CandidateBox[]): Array<{ boxes: CandidateBox[] }> {
  const columns: Array<{ boxes: CandidateBox[] }> = [];
  for (const candidate of candidates) {
    const centerX = candidate.bbox.x + candidate.bbox.width / 2;
    const column = columns.find((item) => {
      const itemCenter = average(item.boxes.map((box) => box.bbox.x + box.bbox.width / 2));
      return Math.abs(itemCenter - centerX) < 110;
    });
    if (column) {
      column.boxes.push(candidate);
    } else {
      columns.push({ boxes: [candidate] });
    }
  }
  return columns;
}

function isReliableUrlColumnReference(box: CandidateBox): boolean {
  return hasKnownDomainTail(box.text ?? "") && box.bbox.width >= 40;
}

function isPartialUrlColumnCandidate(box: CandidateBox): boolean {
  const text = box.text ?? "";
  return /[A-Za-z0-9]\.[A-Za-z0-9]/.test(text) && !hasKnownDomainTail(text);
}

function hasKnownDomainTail(text: string): boolean {
  const tail = text.toLowerCase().match(/\.([a-z0-9]{2,63})(?::\d{2,5})?(?:\/.*)?$/);
  return Boolean(tail && DOMAIN_TLDS.has(tail[1]));
}

function hasUrlishRowContext(text: string): boolean {
  return /wildcard|DSM|DDNS|https?:\/\/|Local|DNS|업데이트/i.test(text);
}

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
}

function extendUrlBoxRange(line: OcrLine, start: number, end: number): { start: number; end: number } {
  const textStart = extendTextMaskedPrefix(line.text, start);
  const wordStart = extendWordMaskedPrefix(line, textStart, end);
  return { start: wordStart, end };
}

function extendTextMaskedPrefix(text: string, start: number): number {
  let prefixStart = start;
  while (prefixStart > 0 && !/\s/.test(text[prefixStart - 1])) {
    prefixStart -= 1;
  }
  return isMaskedDomainPrefix(text.slice(prefixStart, start)) ? prefixStart : start;
}

function extendWordMaskedPrefix(line: OcrLine, start: number, end: number): number {
  if (!line.words?.length) return start;
  const spans = wordSpans(line);
  const firstMatched = spans.find((span) => rangesOverlap(start, end, span.start, span.end));
  if (!firstMatched) return start;
  const previous = spans.filter((span) => span.end <= firstMatched.start).at(-1);
  if (!previous) return start;
  const gap = line.text.slice(previous.end, firstMatched.start);
  const previousText = line.text.slice(previous.start, previous.end);
  if (!/^\s*$/.test(gap) || !isMaskedDomainPrefix(previousText)) return start;
  return previous.start;
}

function isMaskedDomainPrefix(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.endsWith(".")) return false;
  const marker = trimmed.slice(0, -1);
  return marker.length > 0 && !/[A-Za-z0-9가-힣]/.test(marker);
}

function rangeToRect(line: OcrLine, start: number, end: number, category: DetectionCategory): Rect {
  const wordsRect = wordsRangeToRect(line, start, end, category);
  const lineRect = lineRangeToRect(line.bbox, line.text.length, start, end);
  if (!wordsRect) return lineRect;
  if (category === "url" && wordsRect.coverage < 0.7) {
    return unionRects([wordsRect.rect, lineRect]);
  }
  return wordsRect.rect;
}

function wordsRangeToRect(
  line: OcrLine,
  start: number,
  end: number,
  category: DetectionCategory
): { rect: Rect; coverage: number } | null {
  if (!line.words?.length) return null;
  const spans = wordSpans(line);
  const matched = spans.filter((span) => rangesOverlap(start, end, span.start, span.end));
  if (matched.length === 0) return null;
  const covered = matched.reduce((sum, span) => {
    const overlapStart = Math.max(start, span.start);
    const overlapEnd = Math.min(end, span.end);
    return sum + Math.max(0, overlapEnd - overlapStart);
  }, 0);
  return {
    rect: unionRects(matched.map((span) => spanOverlapToRect(span, start, end, category))),
    coverage: covered / Math.max(1, end - start)
  };
}

function wordSpans(line: OcrLine): WordSpan[] {
  let cursor = 0;
  return (line.words ?? []).flatMap((word) => {
    const start = line.text.indexOf(word.text, cursor);
    if (start < 0) return [];
    cursor = start + word.text.length;
    return [{ text: word.text, start, end: cursor, bbox: word.bbox }];
  });
}

function spanOverlapToRect(span: WordSpan, start: number, end: number, category: DetectionCategory): Rect {
  const overlapStart = Math.max(start, span.start);
  const overlapEnd = Math.min(end, span.end);
  const spanLength = Math.max(1, span.end - span.start);
  const x = span.bbox.x + span.bbox.width * ((overlapStart - span.start) / spanLength);
  let width = span.bbox.width * ((overlapEnd - overlapStart) / spanLength);
  if (category === "ip" && overlapEnd < span.end) {
    const suffix = span.text.slice(overlapEnd - span.start);
    const suffixLabel = suffix.match(/^[-·\s]*(?:DSM|활성|Local|관리)/i)?.[0];
    if (suffixLabel) {
      const suffixWidth = suffixLabel.length * span.bbox.height * 0.55;
      width = Math.min(width, Math.max(span.bbox.height * 5, span.bbox.width - suffixWidth - (x - span.bbox.x)));
    }
  }
  return { x, y: span.bbox.y, width, height: span.bbox.height };
}

function unionRects(rects: Rect[]): Rect {
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function lineRangeToRect(lineBox: Rect, textLength: number, start: number, end: number): Rect {
  const safeLength = Math.max(1, textLength);
  return {
    x: lineBox.x + lineBox.width * (start / safeLength),
    y: lineBox.y,
    width: lineBox.width * ((end - start) / safeLength),
    height: lineBox.height
  };
}

function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}
